import { useTranslation } from 'react-i18next'
import type { OrgDetailsSummary, OrgRole } from '../services/api'

interface Props { summary: OrgDetailsSummary }

const nbNO = (n: number) => n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })
const pctNO = (n: number) =>
  n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'

// Translation keys, resolved with t() at render so a language switch applies.
const ROLE_LABELS = {
  kjopt: 'orgRoles.kjopt',
  leaset_inn: 'orgRoles.leasetInn',
  leaset_ut_naering: 'orgRoles.leasetUtNaering',
  leaset_ut_privat: 'orgRoles.leasetUtPrivat',
  leaset_ut_udefinert: 'orgRoles.leasetUtUdefinert',
} as const satisfies Record<OrgRole, string>

const ROLE_BG: Record<OrgRole, string> = {
  kjopt: 'bg-blue-50 dark:bg-blue-900/20',
  leaset_inn: 'bg-teal-50 dark:bg-teal-900/20',
  leaset_ut_naering: 'bg-purple-50 dark:bg-purple-900/20',
  leaset_ut_privat: 'bg-amber-50 dark:bg-amber-900/20',
  leaset_ut_udefinert: 'bg-slate-100 dark:bg-slate-800/40',
}

const ROLE_FG: Record<OrgRole, string> = {
  kjopt: 'text-blue-600 dark:text-blue-400',
  leaset_inn: 'text-teal-600 dark:text-teal-400',
  leaset_ut_naering: 'text-purple-600 dark:text-purple-400',
  leaset_ut_privat: 'text-amber-700 dark:text-amber-400',
  leaset_ut_udefinert: 'text-slate-600 dark:text-slate-300',
}

const ALL_ROLES: OrgRole[] = [
  'kjopt', 'leaset_inn', 'leaset_ut_naering', 'leaset_ut_privat', 'leaset_ut_udefinert',
]

export default function OrgDetailsKpiStrip({ summary }: Props) {
  const { t } = useTranslation('components')
  const sel = summary.selected
  const topRoles: OrgRole[] = ALL_ROLES
    .filter((r) => sel[r] > 0)
    .sort((a, b) => sel[b] - sel[a])
    .slice(0, 2)

  return (
    <div className="card p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('orgDetailsKpiStrip.title')}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-gray-50 dark:bg-dark-border rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('orgDetailsKpiStrip.totalSelected')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{nbNO(sel.total)}</p>
        </div>

        {topRoles.map((role) => (
          <div key={role} className={`p-4 rounded-lg ${ROLE_BG[role]}`}>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t(ROLE_LABELS[role])}</p>
            <p className={`text-2xl font-bold ${ROLE_FG[role]}`}>{nbNO(sel[role])}</p>
          </div>
        ))}

        {Array.from({ length: Math.max(0, 2 - topRoles.length) }).map((_, i) => (
          <div key={`spacer-${i}`} className="hidden md:block" />
        ))}

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('orgDetailsKpiStrip.shareOf')}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {pctNO(summary.selected_share_percent)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t('orgDetailsKpiStrip.rest', { n: nbNO(summary.rest_of_2025_2026.total) })}
          </p>
        </div>
      </div>
    </div>
  )
}
